import { useEffect, useState } from "react";
import { ApiError, api, type ImeiOut, type TipoSolucion } from "@/lib/api";
import { useToast } from "@/ui/Toast";

export type SolucionSeleccion = {
  tipoSolucionId: number | null;
  solucionesImei: Record<number, number> | null;
};

const NUEVO = "__nuevo__";

/**
 * Selector de UN tipo de solución (con opción "+ Crear nuevo…").
 *
 * Vive a nivel de módulo a propósito: antes estaba declarado DENTRO de
 * `SolucionPicker`, así que en cada render del padre se redefinía y React
 * re-montaba el `<input>` — perdía el foco y se borraba el texto en cada
 * tecla, y el `autoFocus` robaba el foco al editor del correo. Ahora es un
 * componente estable con estado de "crear" PROPIO (cada selector el suyo) e
 * input CONTROLADO.
 */
function SelectTipoSolucion({
  tipos,
  val,
  onPick,
  onCrear,
}: {
  tipos: TipoSolucion[];
  val: number | null;
  onPick: (id: number | null) => void;
  onCrear: (nombre: string, aplicar: (id: number) => void) => Promise<void>;
}) {
  const [creando, setCreando] = useState(false);
  const [nombre, setNombre] = useState("");

  function confirmar() {
    void onCrear(nombre, (id) => {
      onPick(id);
      setCreando(false);
      setNombre("");
    });
  }

  return (
    <div className="flex flex-col gap-1">
      <select
        className="input"
        value={creando ? NUEVO : (val ?? "")}
        onChange={(e) => {
          if (e.target.value === NUEVO) {
            setCreando(true);
            return;
          }
          setCreando(false);
          onPick(e.target.value ? Number(e.target.value) : null);
        }}
      >
        <option value="">— elige el tipo de solución —</option>
        {tipos.map((t) => (
          <option key={t.id} value={t.id}>
            {t.nombre}
          </option>
        ))}
        <option value={NUEVO}>+ Crear nuevo…</option>
      </select>
      {creando && (
        <div className="flex gap-1">
          <input
            autoFocus
            className="input flex-1"
            placeholder="Nombre del nuevo tipo"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                confirmar();
              }
              if (e.key === "Escape") {
                setCreando(false);
                setNombre("");
              }
            }}
          />
          <button
            type="button"
            className="btn-ghost text-xs px-2 whitespace-nowrap"
            onClick={confirmar}
          >
            Crear
          </button>
        </div>
      )}
    </div>
  );
}

export default function SolucionPicker({
  imeis,
  value,
  onChange,
}: {
  imeis: ImeiOut[];
  value: SolucionSeleccion;
  onChange: (v: SolucionSeleccion) => void;
}) {
  const toast = useToast();
  const [tipos, setTipos] = useState<TipoSolucion[]>([]);
  const [porEquipo, setPorEquipo] = useState(false);
  const variosImeis = imeis.length > 1;

  async function refresh() {
    setTipos(await api.get<TipoSolucion[]>("/api/tipos-solucion"));
  }
  useEffect(() => {
    void refresh();
  }, []);

  async function crearAlVuelo(nombre: string, aplicar: (id: number) => void) {
    if (!nombre.trim()) return;
    try {
      const t = await api.post<TipoSolucion>("/api/tipos-solucion", {
        nombre: nombre.trim(),
      });
      await refresh();
      aplicar(t.id);
      toast.success("Solución creada", t.nombre);
    } catch (err) {
      toast.error("No se pudo crear", err instanceof ApiError ? err.detail : "Error");
    }
  }

  // Caso sin equipos: un solo selector → caso.
  if (imeis.length === 0) {
    return (
      <div className="space-y-1">
        <label className="text-xs uppercase tracking-wide text-zinc-400">
          Tipo de solución
        </label>
        <SelectTipoSolucion
          tipos={tipos}
          val={value.tipoSolucionId}
          onPick={(id) => onChange({ tipoSolucionId: id, solucionesImei: null })}
          onCrear={crearAlVuelo}
        />
      </div>
    );
  }

  // Caso con equipos: una para todos + opción por equipo.
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-wide text-zinc-400">
        Tipo de solución
      </label>
      {!porEquipo ? (
        <>
          <SelectTipoSolucion
            tipos={tipos}
            val={value.tipoSolucionId}
            onPick={(id) => onChange({ tipoSolucionId: id, solucionesImei: null })}
            onCrear={crearAlVuelo}
          />
          {variosImeis && (
            <div className="flex items-center justify-between text-xs text-amber-300">
              <span>Se aplicará a los {imeis.length} equipos del caso.</span>
              <button
                type="button"
                className="underline hover:text-amber-200"
                onClick={() => {
                  setPorEquipo(true);
                  onChange({ tipoSolucionId: null, solucionesImei: {} });
                }}
              >
                Elegir por equipo
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-2">
          {imeis.map((im) => (
            <div key={im.id} className="flex flex-col gap-1">
              <span className="font-mono text-xs text-zinc-300">{im.imei}</span>
              <SelectTipoSolucion
                tipos={tipos}
                val={value.solucionesImei?.[im.id] ?? null}
                onPick={(id) =>
                  onChange({
                    tipoSolucionId: null,
                    solucionesImei: {
                      ...(value.solucionesImei ?? {}),
                      ...(id ? { [im.id]: id } : {}),
                    },
                  })
                }
                onCrear={crearAlVuelo}
              />
            </div>
          ))}
          <button
            type="button"
            className="text-xs underline text-zinc-400 hover:text-white"
            onClick={() => {
              setPorEquipo(false);
              onChange({ tipoSolucionId: null, solucionesImei: null });
            }}
          >
            ← Una sola para todos
          </button>
        </div>
      )}
    </div>
  );
}
